import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { plantillaApi } from '../plantillaApi';
import {
  blankItemNumberRow,
  itemNumberRows,
  itemNumbersOf,
  itemNumbersValid,
  type ItemNumberRow,
} from '../utils/itemNumbers';
import { sampleItemNumbers } from '../utils/samplePlantillaPost';
import { ItemNumberGrid } from './ItemNumberGrid';

/**
 * Authorize more slots under a post.
 *
 * Its own modal, and its own permission, because a headcount change is a different decision
 * from editing a title. Each slot carries the item number the reader TYPES (16 Oct 2026) —
 * the same `<ItemNumberGrid>` the New plantilla form uses, so the two cannot disagree about
 * what a valid row is. A number is unique across every item there has ever been.
 */
export function AddSlotsModal({
  open,
  positionId,
  onClose,
  onSaved,
}: {
  open: boolean;
  positionId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<ItemNumberRow[]>(() => [blankItemNumberRow()]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = itemNumbersValid(rows) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await plantillaApi.addSlots(positionId, itemNumbersOf(rows));
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not add the slots.', 0);

      setError(failure);

      // A refused number is named by its row (`itemNumbers[2]`), which is the cell's own id.
      focusValidationError(failure, { idPrefix: 'add-slots' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add slots"
      /* A grid rather than one box: `sm` clipped the item-number column. */
      size="md"
      id="add-slots-modal"
      // One to five synthetic numbers, unique to the click so a repeat does not collide.
      autogenerate={{
        onGenerate: () => setRows(itemNumberRows(sampleItemNumbers(1 + Math.floor(Math.random() * 5)))),
        disabled: saving,
      }}
      confirm={{
        id: 'add-slots-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="add-slots-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="add-slots-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <ItemNumberGrid idPrefix="add-slots" rows={rows} onRows={setRows} disabled={saving} />
      </div>
    </Modal>
  );
}
